/**
 * GeofenceGate — location capture + address confirm + (optional) distance flag.
 *
 * Flow (both geo modes always capture location):
 *   1. Capture the surveyor's GPS coordinates.
 *   2. Reverse-geocode and show the detected address + a few nearby candidates
 *      for the surveyor to confirm / pick.
 *   3. STRICT mode: compare the confirmed location to the site's registered
 *      coordinates. If outside the allowed distance, FLAG it (recorded for the
 *      underwriter, optional reason) — the survey is NEVER blocked.
 *      CAPTURE-ONLY mode: no distance check, just record the confirmed address.
 *
 * The flag is emitted as a `GeofenceBypass` alongside the capture; the parent
 * persists both on submit and the Risk drawer surfaces it for audit.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, CheckCircle2, Loader2, AlertTriangle, ShieldAlert } from 'lucide-react';

export interface GeofenceCapture {
  lat: number; lng: number; accuracy_m: number;
  captured_at: string; permission_status: string; source: 'browser' | 'native';
  resolved_address?: string;
}

export interface GeofenceBypass {
  distance_m: number;
  threshold_m: number;
  site_lat?: number;
  site_lng?: number;
  reason: string;
  acknowledged_at: string;
}

export interface SiteGeofenceConfig {
  lat: number;
  lng: number;
  threshold_m: number;
  hard_cap_m: number;
  require_bypass_reason: boolean;
  site_name?: string;
}

interface Props {
  /** 'strict' = flag when outside the allowed distance; 'capture_only' = record only. */
  mode?: 'strict' | 'capture_only';
  /** Backend-supplied site coords + threshold (null = no site coordinates). */
  siteConfig?: SiteGeofenceConfig | null;
  onCapture: (g: GeofenceCapture, bypass?: GeofenceBypass) => void;
  /** Reverse-geocode a coordinate → candidate address strings. */
  reverseGeocode?: (lat: number, lng: number) => Promise<string[]>;
}

const distanceMeters = (a: { lat: number; lng: number }, b: { lat: number; lng: number }): number => {
  const R = 6371000;
  const toRad = (deg: number): number => deg * Math.PI / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLng * sinLng;
  return 2 * R * Math.asin(Math.sqrt(h));
};

export const GeofenceGate: React.FC<Props> = ({ mode = 'strict', siteConfig, onCapture, reverseGeocode }) => {
  const [state, setState] = useState<'idle' | 'requesting' | 'confirm' | 'denied' | 'error'>('idle');
  const [captured, setCaptured] = useState<GeofenceCapture | null>(null);
  const [error, setError] = useState('');
  const [candidates, setCandidates] = useState<string[]>([]);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [reason, setReason] = useState('');

  const strict = mode === 'strict';
  const distance = captured && siteConfig && Number.isFinite(siteConfig.lat)
    ? distanceMeters(captured, siteConfig) : null;
  const flagged = strict && distance != null && distance > (siteConfig?.threshold_m ?? 0);

  const request = (): void => {
    if (!navigator.geolocation) { setState('error'); setError('Geolocation not supported by this browser.'); return; }
    setState('requesting');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const g: GeofenceCapture = {
          lat: pos.coords.latitude, lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          captured_at: new Date().toISOString(),
          permission_status: 'granted', source: 'browser',
        };
        setCaptured(g);
        let list: string[] = [];
        if (reverseGeocode) {
          try { list = await reverseGeocode(g.lat, g.lng); } catch { /* keep coords only */ }
        }
        setCandidates(list);
        setSelectedAddress(list[0] || '');
        setState('confirm');
      },
      (err) => { setState('denied'); setError(err.message || 'Permission denied'); },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  };

  const confirm = (): void => {
    if (!captured) return;
    const cap: GeofenceCapture = { ...captured, resolved_address: selectedAddress || undefined };
    let bypass: GeofenceBypass | undefined;
    if (flagged && siteConfig && distance != null) {
      bypass = {
        distance_m: Math.round(distance),
        threshold_m: siteConfig.threshold_m,
        site_lat: siteConfig.lat,
        site_lng: siteConfig.lng,
        reason: reason.trim(),
        acknowledged_at: new Date().toISOString(),
      };
    }
    onCapture(cap, bypass);
  };

  /* ── Idle / requesting / denied ── */
  if (state === 'idle' || state === 'requesting' || state === 'denied' || state === 'error') {
    return (
      <Card className="border-raptor-blue/40 bg-raptor-blue/5">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-raptor-blue/20 flex items-center justify-center flex-shrink-0">
              <MapPin size={24} className="text-raptor-blue" />
            </div>
            <div className="flex-1">
              <div className="font-headline text-raptor-navy tracking-wide text-lg md:text-xl">
                Confirm your location <Badge variant="warning" className="ml-1">Required</Badge>
              </div>
              <div className="text-base text-raptor-navy/80 font-body mt-2 leading-relaxed">
                We'll capture your current location and detected address. {strict && siteConfig
                  ? <>It'll be checked against the site's registered address (within <b>{siteConfig.threshold_m} m</b>).</>
                  : <>This is recorded with the survey for audit.</>}
              </div>
              {(state === 'denied' || state === 'error') && (
                <div className="mt-2 text-sm text-red-700 flex items-start gap-2 bg-red-50 border border-red-200 rounded p-2">
                  <ShieldAlert size={16} className="flex-shrink-0 mt-0.5" />
                  <div>Location unavailable. <b>{error}</b>. Please enable location access and retry.</div>
                </div>
              )}
              <div className="mt-4">
                <Button size="lg" variant="gradient" className="text-base" onClick={request} disabled={state === 'requesting'} data-testid="geofence-capture">
                  {state === 'requesting'
                    ? <><Loader2 size={16} className="animate-spin" /> Locating…</>
                    : 'Capture my location'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  /* ── Confirm address + (strict) distance flag ── */
  return (
    <Card className={flagged ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}>
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center gap-2">
          {flagged
            ? <AlertTriangle size={18} className="text-amber-600" />
            : <CheckCircle2 size={18} className="text-emerald-600" />}
          <div className="font-headline text-raptor-navy tracking-wide">Confirm your location</div>
        </div>

        <div className="text-xs text-raptor-navy/60 font-tabular">
          {captured?.lat.toFixed(6)}, {captured?.lng.toFixed(6)} · accuracy ±{Math.round(captured?.accuracy_m ?? 0)}m
          {distance != null && (
            <> · <span className={flagged ? 'text-amber-700 font-semibold' : ''}>{Math.round(distance)}m from site</span></>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-xs font-body text-raptor-navy/70 uppercase tracking-wide">Detected address</label>
          {candidates.length > 0 ? (
            <select
              value={selectedAddress}
              onChange={(e) => setSelectedAddress(e.target.value)}
              className="w-full border border-border/70 rounded-md p-2 text-sm font-body bg-white"
              data-testid="geofence-address-select"
            >
              {candidates.map((c, i) => <option key={i} value={c}>{c}</option>)}
            </select>
          ) : (
            <input
              value={selectedAddress}
              onChange={(e) => setSelectedAddress(e.target.value)}
              placeholder="Type the address (optional)"
              className="w-full border border-border/70 rounded-md p-2 text-sm font-body"
              data-testid="geofence-address-input"
            />
          )}
        </div>

        {flagged && (
          <div className="space-y-1">
            <div className="text-sm text-amber-800 bg-amber-100/60 border border-amber-200 rounded p-2 flex items-start gap-2">
              <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" />
              <div>You appear to be <b>{Math.round(distance ?? 0)} m</b> from the site (allowed {siteConfig?.threshold_m} m). This will be flagged for the underwriter — you can still continue.</div>
            </div>
            <label className="text-xs font-body text-raptor-navy/70 uppercase tracking-wide">Reason (optional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. Site entrance is on the far side of the property"
              className="w-full border border-border/70 rounded-md p-2 text-sm font-body"
              data-testid="geofence-flag-reason"
            />
          </div>
        )}

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setState('idle'); setCaptured(null); setReason(''); }} data-testid="geofence-recapture">
            Recapture
          </Button>
          <Button variant="gradient" onClick={confirm} data-testid="geofence-confirm">
            Confirm &amp; continue
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
